import React, { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { supabase } from "./supabaseClient";
import CourseSimilarityPrecomputedGraph from "./CourseSimilarityPrecomputedGraph";
import Auth from "./Auth";
import UpdatePassword from "./UpdatePassword";
import TermsPage from "./TermsPage";
import Intake from "./Intake"; // intake semester checklist
import SemesterCourseIntake from "./SemesterCourseIntake"; // per-semester course selector
import { useNavigate } from "react-router-dom";
import CourseInput from "./CourseInput";
import { CURRENT_SEMESTER, API_BASE_URL, SURVEY_CONFIG } from "./config";
import Upload from "./Upload";
import IntakePrompt from "./IntakePrompt";
import SurpriseButton from "./SurpriseButton";
import TermsModal from "./TermsModal"; // adjust path
import UserInfoPopup from "./UserInfoPopup";
import AccountDropdown from "./AccountDropdown";
import { SemesterProvider } from './SemesterContext';
import SubmissionPage from "./SubmissionPage";
import NotesPopup from "./NotesPopup";
import BaselineSurvey from "./BaselineSurvey";
import SiteTour from "./SiteTour";
import { StickyNote, HelpCircle, MessageCircleQuestion, GraduationCap, Sparkles } from "lucide-react";
import Button from "./components/ui/Button";
import { Analytics } from "@vercel/analytics/react"
import posthog from 'posthog-js'
// --- PostHog Initialization ---
if (process.env.REACT_APP_POSTHOG_KEY && process.env.REACT_APP_POSTHOG_HOST) {
  console.log("🚀 Initializing PostHog with:", process.env.REACT_APP_POSTHOG_HOST);
  posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
    api_host: process.env.REACT_APP_POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: true,
  });
} else {
  console.warn("⚠️ PostHog: Missing REACT_APP_POSTHOG_KEY or REACT_APP_POSTHOG_HOST in .env");
}


//console.log("🟢 Using backend URL:", process.env.REACT_APP_BACKEND_URL);


// Compact brand wordmark for the thin floating header.
function Wordmark() {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-white">
        <GraduationCap className="h-4 w-4" />
      </span>
      <span className="hidden font-display text-[15px] font-semibold leading-none text-ink sm:block">
        Courstellation
      </span>
    </div>
  );
}

// Full-bleed shell: the graph IS the page; a thin header + (graph-owned)
// bottom rail float on top. `search` slot sits in the header center.
function AppShell({ search, nav, children }) {
  return (
    <div className="relative h-screen w-full overflow-hidden bg-canvas">
      <header className="absolute inset-x-0 top-0 z-30 flex h-12 items-center gap-4 border-b border-line/50 bg-canvas/70 px-4 backdrop-blur-md">
        <Wordmark />
        <div className="flex min-w-0 flex-1 justify-center">{search}</div>
        <nav className="flex items-center gap-1">{nav}</nav>
      </header>
      <main className="absolute inset-x-0 bottom-0 top-12">{children}</main>
    </div>
  );
}

// Layout component for shared UI elements
function Layout({ children, search, logout, onShowHelp, onShowUserInfo, onShowNotes, surveyCompleted }) {
  const navigate = useNavigate();

  return (
    <AppShell
      search={search}
      nav={
        <>
          {!surveyCompleted && (
            <Button variant="accent" size="sm" onClick={() => navigate("/survey")} className="flex items-center gap-1 bg-accent hover:bg-accent-dark text-white">
              <Sparkles className="h-4 w-4 text-white animate-pulse" />
              <span className="hidden lg:inline text-white font-semibold">Take Survey</span>
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onShowHelp}>
            <HelpCircle className="h-4 w-4" /> <span className="hidden lg:inline">Help</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate("/question")}>
            <MessageCircleQuestion className="h-4 w-4" /> <span className="hidden lg:inline">Ask</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={onShowNotes} className="notes-btn">
            <StickyNote className="h-4 w-4" /> <span className="hidden lg:inline">Notes</span>
          </Button>
          <Button variant="primary" size="sm" onClick={() => navigate("/intake-prompt")}>
            <GraduationCap className="h-4 w-4" /> <span className="hidden md:inline">Add courses</span>
          </Button>
          <AccountDropdown onSignOut={logout} onShowUserInfo={onShowUserInfo} />
        </>
      }
    >
      {children}
    </AppShell>
  );
}

// Public layout component without authentication controls
function PublicLayout({ children, onShowHelp }) {
  const navigate = useNavigate();

  return (
    <AppShell
      nav={
        <>
          <Button variant="ghost" size="sm" onClick={onShowHelp}>
            <HelpCircle className="h-4 w-4" /> <span className="hidden lg:inline">Help</span>
          </Button>
          <Button variant="primary" size="sm" onClick={() => navigate("/")}>
            Sign In
          </Button>
        </>
      }
    >
      {children}
    </AppShell>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [highlighted, setHighlighted] = useState([]);
  const [conflicted, setConflicted] = useState([]);
  const [currentSemester, setCurrentSemester] = useState(CURRENT_SEMESTER);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const backendUrl = API_BASE_URL;
  const [showTerms, setShowTerms] = useState(false);
  const [showUserInfoPopup, setShowUserInfoPopup] = useState(false);
  const [showNotesPopup, setShowNotesPopup] = useState(false);
  const [userClassYear, setUserClassYear] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const graphRef = useRef(null);
  const [surveyCompleted, setSurveyCompleted] = useState(null);
  const [userGroup, setUserGroup] = useState(null);
  const [priorEnrolledCourses, setPriorEnrolledCourses] = useState([]);

  const handleHighlight = useCallback((newHighlighted) => {
    // Check if newHighlighted is a function
    if (typeof newHighlighted === 'function') {
      // If it is, call it with the current state
      setHighlighted(newHighlighted);
    } else {
      // If not, use it directly as the new state
      setHighlighted(newHighlighted);
    }
  }, []);

  // Function to check conflicts with the backend
  const checkConflicts = useCallback(async (courses, semester) => {
    try {
      const response = await fetch(`${backendUrl}/conflicted_courses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          taken_courses: courses,
          semester: semester
        }),
      });
      const data = await response.json();
      setConflicted(data.conflicted_courses);
    } catch (error) {
      console.error('Error checking conflicts:', error);
      setConflicted([]);
    }
  }, [backendUrl]);

  // --- PostHog Identity Sync (Developer Switch) ---
  useEffect(() => {
    const isIdentifyEnabled = String(process.env.REACT_APP_POSTHOG_IDENTIFY_ENABLED).toLowerCase() === 'true';

    if (user && isIdentifyEnabled) {
      console.log("👤 PostHog: Identifying user:", user.email);
      posthog.identify(user.id, {
        email: user.email,
        class_year: userClassYear
      });
    } else {
      console.log("👤 PostHog: Staying anonymous (Switch is:", process.env.REACT_APP_POSTHOG_IDENTIFY_ENABLED, ")");
      posthog.reset(); // Stay anonymous
    }
  }, [user, userClassYear]);

  // Handler for surprise recommendation
  const handleSurpriseRecommendation = useCallback((courseCodes) => {
    // Highlight the recommended course on the graph
    setHighlighted(courseCodes);
    // Check for conflicts with the recommended course
    checkConflicts(courseCodes, currentSemester);
  }, [checkConflicts, currentSemester]);

  // Effect to handle semester changes
  useEffect(() => {
    // When semester changes, check conflicts with current highlighted list
    checkConflicts(highlighted, currentSemester);
  }, [currentSemester, highlighted, checkConflicts]);

  // Function to check if user has provided their info
  const checkUserInfo = async (user) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        console.error("No valid session token found");
        return false;
      }

      const response = await fetch(`${backendUrl}/check_user_info`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error("Failed to check user info");
        return false;
      }

      const data = await response.json();
      if (data.class_year) setUserClassYear(data.class_year);
      return data.has_info;
    } catch (error) {
      console.error("Error checking user info:", error);
      return false;
    }
  };

  useEffect(() => {
    // Check if we're returning from a recovery email via URL fragment
    if (window.location.hash.includes('type=recovery')) {
      console.log("🔗 URL Fragment detected recovery mode via hash!");
      setRecoveryMode(true);
    }

    supabase.auth.getUser().then(({ data: { user } }) => {
      console.log("🕵️ Initial user fetch:", user?.email);
      setUser(user);
    });

    supabase.auth.onAuthStateChange((event, session) => {
      console.log("🔔 Auth Event Fired:", event, "User:", session?.user?.email);

      if (event === 'PASSWORD_RECOVERY') {
        console.log("🔑 PASSWORD_RECOVERY detected! Setting recovery mode to true.");
        setRecoveryMode(true);
      }

      if (session?.user) {
        setUser(session.user);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setRecoveryMode(false);
      }
    });
  }, []);

  // Pre-fetch the user's class year (for first-year-aware features) without
  // auto-opening the "Tell us about yourself" popup. For the freshman pilot,
  // we don't want to gate the experience on a long form before they've even
  // seen the map — users can fill it later via Account → User Info.
  useEffect(() => {
    if (!user || window.location.pathname === '/public-graph') return;
    checkUserInfo(user); // populates userClassYear as a side-effect
  }, [user, backendUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkSurveyStatus = useCallback(async (user) => {
    if (!user) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;

      const response = await fetch(`${backendUrl}/check_survey_status?survey_config=${SURVEY_CONFIG}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSurveyCompleted(data.completed);
        setUserGroup(data.user_group);
        setPriorEnrolledCourses(data.prior_enrolled_courses || []);
      }
    } catch (err) {
      console.error("Error checking survey status:", err);
    }
  }, [backendUrl]);

  useEffect(() => {
    if (user) {
      checkSurveyStatus(user);
    } else {
      setSurveyCompleted(null);
      setUserGroup(null);
    }
  }, [user, checkSurveyStatus]);

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSurveyCompleted(null);
    setUserGroup(null);
  };

  // UserInfoPopup handlers
  const handleUserInfoSave = async (userInfo) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        console.warn("No session token — skipping save");
      } else {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const response = await fetch(`${backendUrl}/save_user_info`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify(userInfo),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          console.warn("Failed to save user info:", response.status);
        }
      }
    } catch (error) {
      console.warn("Error saving user info (non-blocking):", error);
    } finally {
      setShowUserInfoPopup(false);
      if (pendingUser) {
        setUser(pendingUser);
        setPendingUser(null);
      }
    }
  };

  const handleUserInfoClose = () => {
    setShowUserInfoPopup(false);
    setPendingUser(null);

    // Still proceed with login even if they close the popup
    if (pendingUser) {
      setUser(pendingUser);
    }
  };

  const showUserInfoPopupForUser = (user) => {
    setPendingUser(user);
    setShowUserInfoPopup(true);
  };

  // Only show Auth component if no user and not on public routes
  const isPublicRoute = window.location.pathname === '/public-graph';

  if (recoveryMode) {
    return <UpdatePassword onComplete={() => {
      setRecoveryMode(false);
      setUser(null); // Force re-auth after reset
    }} />;
  }

  if (!user && !isPublicRoute) return <Auth onLogin={setUser} onShowUserInfo={showUserInfoPopupForUser} />;

  if (user && surveyCompleted === null && !isPublicRoute) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto"></div>
          <p className="mt-4 text-sm text-ink-muted">Checking survey status...</p>
        </div>
      </div>
    );
  }

  return (
    <SemesterProvider>
      <Router>
        <Routes>
          {/* Public route is always accessible */}
          <Route
            path="/public-graph"
            element={
              <PublicLayout onShowHelp={() => setShowOnboarding(true)}>
                <CourseSimilarityPrecomputedGraph
                  highlighted={[]}
                  conflicted={[]}
                  setHighlighted={() => { }}
                  setConflicted={() => { }}
                  currentSemester={CURRENT_SEMESTER}
                  onSemesterChange={() => { }}
                  onConflicted={() => { }}
                  onHighlight={() => { }}
                  isPublicMode={true}
                  showOnboarding={showOnboarding}
                  setShowOnboarding={setShowOnboarding}
                />
              </PublicLayout>
            }
          />

          {/* Gating routes based on user group status */}
          {userGroup === "control" ? (
            <>
              <Route 
                path="/survey" 
                element={
                  <BaselineSurvey 
                    user={user} 
                    userGroup={userGroup} 
                    alreadyCompleted={surveyCompleted} 
                    priorEnrolledCourses={priorEnrolledCourses}
                    onComplete={() => setSurveyCompleted(true)}
                  />
                } 
              />
              <Route path="*" element={<Navigate to="/survey" replace />} />
            </>
          ) : (
            <>
              <Route
                path="/"
                element={
                  <Layout
                    logout={logout}
                    onShowHelp={() => setShowOnboarding(true)}
                    onShowUserInfo={() => showUserInfoPopupForUser(user)}
                    onShowNotes={() => setShowNotesPopup(true)}
                    surveyCompleted={surveyCompleted}
                    search={
                      <CourseInput
                        onHighlight={handleHighlight}
                        onConflicted={setConflicted}
                        currentSemester={currentSemester}
                        highlighted={highlighted}
                      />
                    }
                  >
                    <CourseSimilarityPrecomputedGraph
                      highlighted={highlighted}
                      conflicted={conflicted}
                      setHighlighted={setHighlighted}
                      setConflicted={setConflicted}
                      currentSemester={currentSemester}
                      onSemesterChange={setCurrentSemester}
                      onConflicted={setConflicted}
                      onHighlight={handleHighlight}
                      showOnboarding={showOnboarding}
                      setShowOnboarding={setShowOnboarding}
                      captureRef={graphRef}
                    />
                    <SurpriseButton onSurpriseRecommendation={handleSurpriseRecommendation} />
                  </Layout>
                }
              />
              <Route 
                path="/survey" 
                element={
                  <BaselineSurvey 
                    user={user} 
                    userGroup={userGroup} 
                    alreadyCompleted={surveyCompleted} 
                    priorEnrolledCourses={priorEnrolledCourses}
                    onComplete={() => setSurveyCompleted(true)}
                  />
                } 
              />
              <Route path="/intake-prompt" element={<IntakePrompt />} />
              <Route path="question" element={<SubmissionPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/intake" element={<Intake />} />
              <Route path="/intake/courses/:index" element={<SemesterCourseIntake />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
        <TermsModal />
        <NotesPopup
          isOpen={showNotesPopup}
          onClose={() => setShowNotesPopup(false)}
          graphRef={graphRef}
          classYear={userClassYear}
        />
        <UserInfoPopup
          isOpen={showUserInfoPopup}
          onClose={handleUserInfoClose}
          onSave={handleUserInfoSave}
        />
        {/* Live spotlight tour replaces the old static onboarding card. */}
        {user && showOnboarding && (
          <SiteTour
            steps={[
              {
                selector: null, placement: "center",
                title: "Welcome to Visual Open Curriculum",
                body: "Every course at Amherst, plotted by what it's about. Courses that explore similar ideas sit close together — even across departments. Let's take a minute to look around.",
              },
              {
                selector: ".map-svg", placement: "top",
                title: "This is your curriculum",
                body: "Each dot is a course. Sciences cluster here, humanities there, and the interesting overlaps live in between. Scroll to zoom in, drag to pan, and sweep your cursor — codes light up near it. Click any dot to see the course's details and its closest neighbors.",
              },
              {
                selector: ".map-svg", placement: "top",
                title: "Spotted a tiny bubble?",
                body: "Small, faded bubbles are prerequisite courses that aren't offered this semester — but they're the building blocks for courses that are. They show up so you can see the full path: if something you want requires a smaller bubble, that's a course to put on your radar for a future semester.",
              },
              {
                selector: ".legend-toggle", placement: "left",
                title: "Want the color key?",
                body: "We hide the legend by default — we don't want a label like 'this region is X' nudging you toward staying in one field. Click here whenever you'd like to see which color is which department.",
              },
              {
                selector: ".search-pill", placement: "bottom",
                title: "Find a course",
                body: "Search by code or title with Keyword (e.g. MATH-111 or “Legal Theory”). Or switch to Semantic and describe an idea (“something about climate justice”). Matches highlight on the map.",
              },
              {
                selector: ".conflicts-toggle", placement: "left",
                title: "Build a workable schedule",
                body: "Pick a course, then flip Eliminate conflicts on — any other course whose time conflicts disappears from the map. Lets you see what could actually fit next to it.",
              },
              {
                selector: ".surprise-btn", placement: "left",
                title: "When you don't know what you want",
                body: "Surprise me finds a course from a corner of the curriculum you haven't explored yet — but that still connects to what you already love.",
              },
              {
                selector: ".notes-btn", placement: "bottom",
                title: "Meet your advisor companion",
                body: "Before each advising meeting, drop your goals and questions into Notes. We'll open it now so you can try it out.",
                onEnter: () => setShowNotesPopup(true),
              },
              {
                selector: ".notes-questions", placement: "left",
                title: "Try answering one prompt",
                body: "These are questions advisors actually ask. Pick the one that resonates most and type a sentence — everything autosaves, and you can email a snapshot to your advisor when you're ready.",
              },
              {
                selector: null, placement: "center",
                title: "You're ready",
                body: "Explore freely. Click any course to see details. Hit Help in the top bar anytime to re-take this tour.",
              },
            ]}
            onFinish={() => {
              setShowOnboarding(false);
              localStorage.setItem("hasSeenOnboarding", "true");
            }}
          />
        )}
        <Analytics />
      </Router>
    </SemesterProvider>
  );
}

export default App;